import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BillingCycle, PlanType } from '../../types/billing';
import { PlanSelector } from '../billing/PlanSelector';

import { API_URL } from '../../constants/urls';

interface CreateOrganizationFlowProps {
  open: boolean;
  onClose: () => void;
  onOrganizationCreated: (org: any) => void;
}

interface OrganizationData {
  name: string;
  slug: string;
  currency: string;
}

export const CreateOrganizationFlow: React.FC<CreateOrganizationFlowProps> = ({
  open,
  onClose,
  onOrganizationCreated,
}) => {
  const { token } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Organization data
  const [orgData, setOrgData] = useState<OrganizationData>({
    name: '',
    slug: '',
    currency: 'GBP',
  });

  // Plan selection data
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('free');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  // Auto-generate slug from name
  React.useEffect(() => {
    if (!slugManuallyEdited && orgData.name) {
      const slug = orgData.name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      setOrgData((prev) => ({ ...prev, slug }));
    }
  }, [orgData.name, slugManuallyEdited]);

  const validateOrgData = (): boolean => {
    if (!orgData.name.trim()) {
      setError('Organization name is required');
      return false;
    }
    if (!orgData.slug.trim()) {
      setError('Organization slug is required');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(orgData.slug)) {
      setError('Slug must be lowercase letters, numbers, or hyphens');
      return false;
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateOrgData()) {
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handlePlanSelect = (plan: PlanType, cycle: BillingCycle) => {
    setSelectedPlan(plan);
    setBillingCycle(cycle);
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      // Always create organization first (as free plan)
      const response = await axios.post(`${API_URL}/organizations`, orgData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newOrg = response.data;
      onOrganizationCreated(newOrg);

      // If paid plan selected, create subscription for the new organization
      if (selectedPlan !== 'free') {
        try {
          // Create subscription checkout for the newly created organization
          const checkoutResponse = await axios.post(
            `${API_URL}/billing/subscribe?organization_id=${newOrg.id}&plan_type=${selectedPlan}&billing_cycle=${billingCycle}&editor_count=1`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          // Store org info for return
          sessionStorage.setItem('subscribingOrgId', newOrg.id);

          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.data.checkout_url;
        } catch (checkoutErr: any) {
          // console.error removed
          // Organization was created, just couldn't set up payment
          setError(
            "Organization created successfully, but couldn't set up payment. " +
              'You can upgrade your plan from the billing settings.',
          );
          // Close after a delay to show the message
          setTimeout(() => {
            onClose();
          }, 5000);
        }
      } else {
        // Free plan - just close
        onClose();
      }
    } catch (err: any) {
      // console.error removed
      setError(
        err.response?.data?.detail || 'Failed to complete organization setup',
      );
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Organization Details', 'Select Plan'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Create New Organization
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Organization Information
              </Typography>
              <TextField
                label="Organization Name"
                value={orgData.name}
                onChange={(e) => {
                  setOrgData({ ...orgData, name: e.target.value });
                  setSlugManuallyEdited(false);
                }}
                fullWidth
                margin="normal"
                required
                helperText="The display name for your organization"
              />

              <TextField
                label="Organization Slug"
                value={orgData.slug}
                onChange={(e) => {
                  setOrgData({ ...orgData, slug: e.target.value });
                  setSlugManuallyEdited(true);
                }}
                fullWidth
                margin="normal"
                required
                helperText="Unique identifier for URLs (lowercase letters, numbers, hyphens)"
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Currency</InputLabel>
                <Select
                  value={orgData.currency}
                  label="Currency"
                  onChange={(e) =>
                    setOrgData({ ...orgData, currency: e.target.value })
                  }
                >
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onClose} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!orgData.name || !orgData.slug}
                >
                  Next: Choose Plan
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Choose Your Plan
              </Typography>

              {/* Payment Processing Notice */}
              {selectedPlan !== 'free' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <AlertTitle>How this works</AlertTitle>
                  Your organization will be created immediately with a free
                  plan. After completing payment, your plan will be
                  automatically upgraded to {selectedPlan}.
                </Alert>
              )}

              <PlanSelector
                currentPlan="free"
                currentEditors={1}
                selectedPlan={selectedPlan}
                onSelectPlan={handlePlanSelect}
              />

              <Box
                sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}
              >
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={loading}
                >
                  {loading
                    ? 'Processing...'
                    : selectedPlan === 'free'
                      ? 'Create Free Organization'
                      : `Continue to Payment (${selectedPlan === 'starter' ? 'Starter' : 'Professional'} ${billingCycle === 'annual' ? 'Annual' : 'Monthly'})`}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
